/**
 * `@tagadapay/analytics/redact`: last line of defence on free text (A3). Pure,
 * no runtime dependency: the browser `before_send` chain, the server
 * analytics and the AI trace emitter all run it.
 *
 * It does not catch names. Names are handled by replay masking, autocapture
 * `mask_all_text` and the AI allowlist (spec F, E6).
 */

import { PERSON_PII_EXCEPTIONS, PII_KEY_REGEX } from './registry/apps';

export const REDACTED = '[redacted]';

const IBAN = /\b[A-Z]{2}\d{2}(?: ?[A-Z0-9]){11,30}\b/g;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// A run of digits and phone separators; redacted when it holds 9 digits or more.
const PHONE_LIKE = /[+(]?\d[\d\s().-]{7,}\d/g;
const DIGIT_RUN = /\d{6,}/g;
// A date of birth: a date whose year is 1900 to 2012 (an adult today), ISO or day/month first.
const BIRTH_DATE = /\b(?:(?:19\d{2}|20(?:0\d|1[0-2]))[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.](?:19\d{2}|20(?:0\d|1[0-2])))\b/g;
// The same, with the month in words, English or French: "14 March 1984", "14th of March, 1984", "March 14, 1984", "1er mars 1984", "14 févr. 1984".
const MONTH =
  '(?:jan(?:uary|v(?:ier)?)?|feb(?:ruary)?|f[ée]v(?:rier|r)?|mar(?:ch|s)?|apr(?:il)?|avr(?:il)?|may|mai|june?|juin|' +
  'july?|juil(?:let)?|aug(?:ust)?|ao[uû]t|sep(?:t(?:ember|embre)?)?|oct(?:ober|obre)?|nov(?:ember|embre)?|dec(?:ember)?|d[ée]c(?:embre)?)';
const BIRTH_YEAR = '(?:19\\d{2}|20(?:0\\d|1[0-2]))';
const BIRTH_DATE_WORDS = new RegExp(
  `\\b(?:\\d{1,2}(?:st|nd|rd|th|er)?\\s+(?:of\\s+)?${MONTH}\\.?,?\\s+${BIRTH_YEAR}|${MONTH}\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+${BIRTH_YEAR})\\b`,
  'gi',
);
// An identity-document number (passport, ID card): 6 to 12 capitals and digits, 2 letters and 4 digits at least.
const ID_NUMBER = /\b(?=(?:[0-9]*[A-Z]){2})(?=(?:[A-Z]*[0-9]){4})[A-Z0-9]{6,12}\b/g;

function digitCount(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i += 1) if (s.charCodeAt(i) >= 48 && s.charCodeAt(i) <= 57) n += 1;
  return n;
}

/**
 * Emails, phone numbers (9 digits or more, a SSN included), digit runs of 6 or
 * more, IBAN-like strings, dates of birth and identity-document numbers become
 * `[redacted]`; the result is cut to `max` chars.
 */
export function redactText(s: string, max = 120): string {
  if (typeof s !== 'string') return s;
  const out = s
    .replace(IBAN, REDACTED)
    .replace(EMAIL, REDACTED)
    .replace(BIRTH_DATE, REDACTED)
    .replace(BIRTH_DATE_WORDS, REDACTED)
    .replace(ID_NUMBER, REDACTED)
    .replace(PHONE_LIKE, (m) => (digitCount(m) >= 9 ? REDACTED : m))
    .replace(DIGIT_RUN, REDACTED);
  return out.length > max ? out.slice(0, max) : out;
}

/** True for a property key on the PII denylist. `$` keys belong to PostHog and are never matched. */
export function isPiiKey(key: string): boolean {
  return !key.startsWith('$') && PII_KEY_REGEX.test(key);
}

type Props = Record<string, unknown>;

function isPlainObject(v: unknown): v is Props {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function scrubPerson(set: unknown): unknown {
  if (!isPlainObject(set)) return set;
  const out: Props = {};
  for (const [k, v] of Object.entries(set)) {
    if (isPiiKey(k) && !(PERSON_PII_EXCEPTIONS as readonly string[]).includes(k)) continue;
    out[k] = v;
  }
  return out;
}

function scrubException(entry: unknown): unknown {
  if (!isPlainObject(entry) || typeof entry['value'] !== 'string') return entry;
  return { ...entry, value: redactText(entry['value']) };
}

function scrubElement(el: unknown): unknown {
  if (!isPlainObject(el) || typeof el['$el_text'] !== 'string') return el;
  return { ...el, $el_text: redactText(el['$el_text']) };
}

/**
 * Drops PII-denylist keys (`$set`/`$set_once` keep `email` and `name`, the
 * merchant user's own), and runs `redactText` over `$el_text`,
 * `$elements_chain`, `$elements[].$el_text` and `$exception_list[].value`.
 * Returns a new object; the input is not modified.
 */
export function scrubProperties<T extends Record<string, unknown>>(props: T): T {
  const out: Props = {};
  for (const [key, value] of Object.entries(props)) {
    if (isPiiKey(key)) continue;
    switch (key) {
      case '$set':
      case '$set_once':
        out[key] = scrubPerson(value);
        break;
      case '$el_text':
        out[key] = typeof value === 'string' ? redactText(value) : value;
        break;
      case '$elements_chain':
        // The chain is structured: redact inside it, never cut it.
        out[key] = typeof value === 'string' ? redactText(value, Number.POSITIVE_INFINITY) : value;
        break;
      case '$elements':
        out[key] = Array.isArray(value) ? value.map(scrubElement) : value;
        break;
      case '$exception_list':
        out[key] = Array.isArray(value) ? value.map(scrubException) : value;
        break;
      default:
        out[key] = value;
    }
  }
  return out as T;
}
